import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

/**
 * Kit (formerly ConvertKit) Subscription Form Component
 * Embeds the Kit subscription form script
 * 
 * @param {Object} props
 * @param {string} props.variant - 'inline' | 'card' | 'minimal' - visual style variant
 * @param {string} props.title - Optional title above the form
 * @param {string} props.description - Optional description text
 * @param {string} props.className - Additional CSS classes
 */
export default function KitSubscriptionForm({ 
  variant = 'card', 
  title,
  description,
  className = '' 
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear any existing content
    containerRef.current.innerHTML = '';

    // Create and append the script
    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('data-uid', 'f63efd6bac');
    script.src = 'https://greengasturbines.kit.com/f63efd6bac/index.js';
    containerRef.current.appendChild(script);

    return () => {
      // Cleanup on unmount
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, []);

  if (variant === 'minimal') {
    return (
      <div ref={containerRef} className={`kit-form-minimal ${className}`} />
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`kit-form-inline ${className}`}>
        {title && (
          <h3 className="text-lg font-semibold text-secondary-900 mb-2">{title}</h3>
        )}
        {description && (
          <p className="text-gray-600 mb-4 text-sm">{description}</p>
        )}
        <div ref={containerRef} />
      </div>
    );
  }

  // Default 'card' variant
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className={`bg-gradient-to-br from-primary-50 to-emerald-50 rounded-2xl p-8 border border-primary-100 shadow-lg ${className}`}
    >
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-4">
          <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-secondary-900 mb-2">
          {title || 'Stay Updated on Clean Energy'}
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          {description || 'Get the latest insights on hydrogen turbines, energy transition strategies, and industry trends delivered to your inbox.'}
        </p>
      </div>
      <div ref={containerRef} className="kit-form-container min-h-[120px]" />
      <p className="text-xs text-gray-500 text-center mt-4">
        No spam, ever. Unsubscribe anytime.
      </p>
    </motion.div>
  );
}
